// Local copy of three/addons/tsl/display/MotionBlur.js with two bug fixes:
//
// 1. The stock addon pre-samples the center pixel into `colorResult`, then
//    loops N times adding more samples, but divides by N. That's N+1 samples
//    in the numerator against N in the denominator — a uniform brightness
//    boost of (N+1)/N (~6.25% at N=16) applied on every pixel, even when
//    velocity is exactly zero.
// 2. The stock addon's offset factor `i/(N-1) - 0.5` with i∈[1..N] produces
//    an asymmetric range (e.g. [-0.433, +0.567] for N=16), biasing the blur
//    forward along the velocity vector instead of centering on the pixel.
//
// This version loops N times total (i∈[0..N-1]), with offset factor
// `i/(N-1) - 0.5` giving an exact [-0.5, +0.5] symmetric range, and divides
// by N. When velocity == 0 every sample collapses onto the center pixel,
// so the result is exactly the input pixel (no brightness shift).
//
// Kept in-repo instead of patched upstream because node_modules shouldn't be
// edited in-place.
import { Fn, float, int, uv, Loop, vec4 } from 'three/tsl';
import type { Node } from 'three/webgpu';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const motionBlurFn = Fn((args: any) => {
  const [inputNode, velocity, numSamples = int(16)] = args;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sampleColor = (uvs: any) => inputNode.sample(uvs);
  const uvs = uv();
  const fSamples = float(numSamples);
  const colorResult = vec4(0).toVar();

  Loop(
    { start: int(0), end: numSamples, type: 'int', condition: '<' },
    ({ i }) => {
      const offset = velocity.mul(float(i).div(fSamples.sub(1)).sub(0.5));
      colorResult.addAssign(sampleColor(uvs.add(offset)));
    }
  );

  colorResult.divAssign(fSamples);
  return colorResult;
});

export const motionBlur = motionBlurFn as unknown as (
  inputNode: Node,
  velocity: Node,
  numSamples?: Node
) => Node<'vec4'>;
